import type { ScheduleInterval } from '@skystone/shared';

const STOCKHOLM_TZ = 'Europe/Stockholm';

/** Stockholm local hour + minute and day-of-week when each schedule interval runs. */
const SCHEDULE_STOCKHOLM: Record<ScheduleInterval, { stockholmHour: number; stockholmMinute?: number; stockholmDayOfWeek?: number }> = {
  daily: { stockholmHour: 8 },
  // PRODOPSAI-436: 07:30 Stockholm — 30 min before the standard daily slot
  // so the Jira-issues sync drains the worker queue before the Fabric block.
  daily_early: { stockholmHour: 7, stockholmMinute: 30 },
  // PRODOPSAI-546: 09:30 Stockholm — after the 08:00 daily block has drained.
  daily_late: { stockholmHour: 9, stockholmMinute: 30 },
  weekly: { stockholmHour: 8, stockholmDayOfWeek: 1 }, // Monday
};

function getTimezoneOffsetHours(date: Date, timeZone: string): number {
  const timeZoneName = new Intl.DateTimeFormat('en-US', {
    timeZone,
    timeZoneName: 'shortOffset',
  })
    .formatToParts(date)
    .find((part) => part.type === 'timeZoneName')?.value;

  const match = timeZoneName?.match(/GMT([+-]\d{1,2})(?::(\d{2}))?/);
  if (!match) {
    return 0;
  }

  const hours = Number.parseInt(match[1], 10);
  const minutes = match[2] ? Number.parseInt(match[2], 10) : 0;
  return hours + (hours >= 0 ? minutes / 60 : -minutes / 60);
}

function getUtcHourForStockholmHour(date: Date, stockholmHour: number): number {
  const stockholmOffset = getTimezoneOffsetHours(date, STOCKHOLM_TZ);
  const utcHour = (stockholmHour - stockholmOffset + 24) % 24;
  return Math.floor(utcHour);
}

/**
 * Build a human-readable schedule description with the execution time
 * converted to the user's local timezone.
 *
 * Examples (for a CET user):
 *   daily  -> "Daily at 08:00"
 *   weekly -> "Monday at 08:00"
 */
export function formatScheduleLabel(interval: ScheduleInterval): string {
  const { stockholmHour, stockholmMinute = 0, stockholmDayOfWeek } = SCHEDULE_STOCKHOLM[interval];

  // Build a reference Date at the correct UTC hour for the Stockholm time.
  const ref = new Date();
  const utcHour = getUtcHourForStockholmHour(ref, stockholmHour);
  ref.setUTCHours(utcHour, stockholmMinute, 0, 0);

  if (stockholmDayOfWeek !== undefined) {
    // Shift to the correct weekday (Monday = 1)
    const currentDay = ref.getUTCDay(); // 0=Sun … 6=Sat
    const diff = stockholmDayOfWeek - currentDay;
    ref.setUTCDate(ref.getUTCDate() + diff);
  }

  const localTime = ref.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  if (interval === 'weekly') {
    const localDay = ref.toLocaleDateString([], { weekday: 'long' });
    return `${localDay} at ${localTime}`;
  }

  // `daily`, `daily_early` and `daily_late` all render as "Daily at HH:MM" —
  // the actual time differs (08:00 / 07:30 / 09:30) and is reflected in localTime.
  return `Daily at ${localTime}`;
}

/** Resolve the IANA timezone name shown in the browser (e.g. "Europe/Stockholm"). */
export function getUserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}
